import path from 'node:path'

import { debugFn } from '@socketsecurity/registry/lib/debug'
import { logger } from '@socketsecurity/registry/lib/logger'

import { extractBazelToMaven } from './extract_bazel_to_maven.mts'
import { extractBazelToPypi } from './extract_bazel_to_pypi.mts'
import { DRY_RUN_BAILING_NOW, SOCKET_JSON } from '../../../constants.mts'
import { commonFlags } from '../../../flags.mts'
import { checkCommandInput } from '../../../utils/check-input.mts'
import { InputError } from '../../../utils/errors.mts'
import { getOutputKind } from '../../../utils/get-output-kind.mts'
import { meowOrExit } from '../../../utils/meow-with-subcommands.mts'
import { getFlagListOutput } from '../../../utils/output-formatting.mts'
import { readOrDefaultSocketJson } from '../../../utils/socket-json.mts'

import type { ExtractBazelStatus } from './extract_bazel_to_maven.mts'
import type {
  CliCommandConfig,
  CliCommandContext,
} from '../../../utils/meow-with-subcommands.mts'

const config: CliCommandConfig = {
  commandName: 'bazel',
  description:
    '[beta] Bazel SBOM support — generate manifest files for a Bazel project (Maven, PyPI)',
  hidden: false,
  flags: {
    ...commonFlags,
    bazel: {
      type: 'string',
      description:
        'Path to bazel/bazelisk binary; default: $(which bazelisk) || $(which bazel)',
    },
    bazelFlags: {
      type: 'string',
      description:
        'Bazel command flags, with quoted values and literal backslashes',
    },
    bazelOutputBase: {
      type: 'string',
      description: 'Bazel --output_base for read-only-cache CI environments',
    },
    bazelRc: {
      type: 'string',
      description: 'Path to additional .bazelrc fragments forwarded to bazel',
    },
    ecosystem: {
      type: 'string',
      isMultiple: true,
      description:
        'Ecosystem(s) to extract; repeatable. Supported: maven, pypi. Default: maven.',
    },
    out: {
      type: 'string',
      description:
        'Output directory for generated manifests; default: ./.socket/bazel-manifests/',
    },
    perRepoTimeout: {
      type: 'number',
      description:
        'Per-hub bazel cquery timeout in milliseconds; default: 120000',
    },
    verbose: {
      type: 'boolean',
      description:
        'Emit bounded Bazel diagnostics with argv, duration, exit status, and output sizes',
    },
  },
  help: (command, config) => `
    Usage
      $ ${command} [options] [CWD=.]

    Options
      ${getFlagListOutput(config.flags)}

    [beta] Generates Bazel SBOM manifests for Maven (\`maven_install.json\`)
    by running \`bazel query\` against discovered dependency repos.
    PyPI requirements generation is available with \`--ecosystem pypi\`.
    Output is consumed by
    \`socket scan create\`'s server-side parser.

    --ecosystem may be repeated to select which ecosystems to extract.
    When omitted, Maven is generated by default. PyPI is explicit opt-in.

    Note: this command generates dependency manifests for Bazel workspaces.
    It does not run reachability analysis.

    Native Windows support currently covers Maven. Use PowerShell or
    Command Prompt with a working Python 3 python.exe and a JDK on PATH.
    PyPI extraction on Windows is not supported yet.

    Set SOCKET_BAZEL_OUTPUT_ROOT to a short, writable parent directory,
    such as C:/sb, to shorten connector-owned Bazel output paths. Each
    invocation gets a separate directory. An explicit --bazel-output-base
    takes precedence for Bazel's cache and is kept after the scan. The
    scan only stops Bazel servers it started.

    In --bazel-flags, single or double quotes at the start of a value or
    after = group values containing spaces. Backslashes stay literal. Double a matching quote to include
    it within a quoted value. Shell variables are not expanded.

    Maven hub discovery: under Bzlmod, hubs are enumerated from
    \`bazel mod show_extension\` and filtered to the root module's own hubs.
    Under legacy WORKSPACE mode (no \`show_extension\`), only conventionally
    named hubs are probed (\`maven\`, \`maven_install\`, \`maven_dev\`, …). A hub
    with a non-conventional name that \`show_extension\` does not enumerate is
    not discovered yet; a flag to name extra hubs is planned.

    To generate AND upload in one step, use \`socket scan create --auto-manifest\`
    instead — it detects Bazel workspaces, generates Maven manifests by
    default, and uploads the result. This subcommand is for generation only.

    Examples
      $ ${command} .
      $ ${command} --ecosystem pypi .
      $ ${command} --ecosystem maven --ecosystem pypi .
      $ ${command} --bazel=/usr/local/bin/bazelisk .
  `,
}

// The explicit `socket manifest bazel` command gives each hub more time than
// the auto-manifest path: a user running it directly is waiting on this one
// extraction, whereas auto-manifest must not stall the wider scan. Auto's
// shorter default lives in extract_bazel_to_maven.mts.
const EXPLICIT_PER_REPO_TIMEOUT_MS = 120_000

export const cmdManifestBazel = {
  description: config.description,
  hidden: config.hidden,
  run,
}

export type EcosystemOutcome = {
  ecosystem: 'maven' | 'pypi'
  status: ExtractBazelStatus
  manifestPaths: string[]
  // Machine-readable completeness signal. True only when the ecosystem's
  // extraction was complete; a `partial` upload sets this false so the CLI
  // surfaces it honestly (exit 0 + prominent warning) rather than as plain
  // success.
  complete: boolean
}

// Pure outcome-matrix evaluator. Exported so dispatcher behavior can be
// unit-tested without spawning the CLI binary. Throws InputError on
// failures that must propagate to a non-zero CLI exit; returns void on
// success.
//
// - `complete`/`partial` both count as produced output (>=1 manifest).
//   `partial` additionally warns — a known-incomplete SBOM is still emitted,
//   not a hard error.
// - `hardFailure`: the ecosystem was detected (or the runner crashed) but
//   wrote zero manifests. Always a non-zero exit, even when another
//   ecosystem succeeded.
// - `noEcosystem`: genuinely absent ecosystem. Auto-detect mode tolerates it
//   when at least one other ecosystem produced output; explicit mode treats
//   it as an error (the user requested an ecosystem that isn't there).
export function evaluateEcosystemOutcomes(
  outcomes: readonly EcosystemOutcome[],
  isExplicit: boolean,
): void {
  const produced = outcomes.filter(
    o =>
      (o.status === 'complete' || o.status === 'partial') &&
      o.manifestPaths.length > 0,
  )
  const hardFailures = outcomes.filter(o => o.status === 'hardFailure')
  const noDiscoveries = outcomes.filter(o => o.status === 'noEcosystem')

  // Surface a machine-readable completeness signal for every produced
  // ecosystem so a partial upload is never presented as a complete one. The
  // per-workspace / per-hub detail is written to the manifest dir's
  // completeness summary by the extractor; this is the human-facing echo.
  for (const outcome of produced) {
    logger.info(
      `Bazel ${outcome.ecosystem} extraction status: ${outcome.status} (complete=${outcome.complete}).`,
    )
  }

  for (const partial of outcomes) {
    if (partial.status === 'partial') {
      logger.warn(
        `WARNING: Bazel ${partial.ecosystem} manifest generation was PARTIAL. The uploaded SBOM is known-incomplete and may under-report dependencies; review the completeness summary before relying on the results.`,
      )
    }
  }

  if (!isExplicit) {
    if (hardFailures.length) {
      throw new InputError(
        `Bazel auto-manifest generation hit hard failure(s) in ecosystem(s): ${hardFailures.map(f => f.ecosystem).join(', ')}.`,
      )
    }
    if (produced.length) {
      return
    }
    if (noDiscoveries.length === outcomes.length) {
      throw new InputError(
        'No supported Bazel ecosystems detected (maven, pypi). Ensure rules_jvm_external, rules_python pip_parse/pip_install/pip_repository, or pip.parse is configured.',
      )
    }
    return
  }

  // Explicit mode: every requested ecosystem must produce output. A partial
  // run counts (it wrote manifests); absent or hard-failed ecosystems error.
  if (noDiscoveries.length) {
    throw new InputError(
      `No Bazel rules found for explicitly requested ecosystem(s): ${noDiscoveries.map(f => f.ecosystem).join(', ')}.`,
    )
  }
  if (hardFailures.length) {
    throw new InputError(
      `Bazel manifest generation failed for explicitly requested ecosystem(s): ${hardFailures.map(f => f.ecosystem).join(', ')}.`,
    )
  }
}

// Map the legacy PyPI result shape (single manifestPath + ok/noEcosystem
// booleans) into the shared status vocabulary so both ecosystems flow through
// one success gate. PyPI has no partial state. Only a `complete` outcome
// carries a manifest path; `noEcosystem`/`hardFailure` carry none, preserving
// the invariant that a non-success outcome produced no usable output (a
// detected-but-empty PyPI run writes a stub file but is still a hard failure,
// and that stub must not be surfaced as produced output).
function pypiOutcome(result: {
  manifestPath?: string | undefined
  noEcosystemFound?: boolean | undefined
  ok: boolean
}): { complete: boolean; manifestPaths: string[]; status: ExtractBazelStatus } {
  if (result.noEcosystemFound) {
    return { complete: false, manifestPaths: [], status: 'noEcosystem' }
  }
  if (result.ok && result.manifestPath) {
    return {
      complete: true,
      manifestPaths: [result.manifestPath],
      status: 'complete',
    }
  }
  return { complete: false, manifestPaths: [], status: 'hardFailure' }
}

async function run(
  argv: string[] | readonly string[],
  importMeta: ImportMeta,
  { parentName }: CliCommandContext,
): Promise<void> {
  const cli = meowOrExit({
    argv,
    config,
    importMeta,
    parentName,
  })

  const { json = false, markdown = false } = cli.flags

  const dryRun = !!cli.flags['dryRun']

  // TODO: Implement json/md further.
  const outputKind = getOutputKind(json, markdown)

  let [cwd = '.'] = cli.input
  // Note: path.resolve vs .join:
  // If given path is absolute then cwd should not affect it.
  cwd = path.resolve(process.cwd(), cwd)

  const sockJson = readOrDefaultSocketJson(cwd)

  debugFn(
    'inspect',
    `override: ${SOCKET_JSON} bazel`,
    sockJson?.defaults?.manifest?.bazel,
  )

  const { ecosystem } = cli.flags
  let { bazel, bazelFlags, bazelOutputBase, bazelRc, out, verbose } = cli.flags
  let perRepoTimeout = cli.flags['perRepoTimeout'] as number | undefined

  // Set defaults for any flag/arg that is not given. Check socket.json first.
  if (bazel) {
    bazel = path.resolve(process.cwd(), String(bazel))
  } else {
    const defaultBazel =
      sockJson.defaults?.manifest?.bazel?.bazel ??
      sockJson.defaults?.manifest?.bazel?.bin
    if (defaultBazel) {
      bazel = defaultBazel
      logger.info(`Using default --bazel from ${SOCKET_JSON}:`, bazel)
    }
    // Otherwise leave undefined; resolveBazelBinary performs the PATH
    // lookup for bazelisk/bazel.
  }
  if (!bazelFlags) {
    if (sockJson.defaults?.manifest?.bazel?.bazelFlags) {
      bazelFlags = sockJson.defaults?.manifest?.bazel?.bazelFlags
      logger.info(
        `Using default --bazel-flags from ${SOCKET_JSON}:`,
        bazelFlags,
      )
    } else {
      bazelFlags = ''
    }
  }
  if (!bazelOutputBase) {
    if (sockJson.defaults?.manifest?.bazel?.bazelOutputBase) {
      bazelOutputBase = sockJson.defaults?.manifest?.bazel?.bazelOutputBase
      logger.info(
        `Using default --bazel-output-base from ${SOCKET_JSON}:`,
        bazelOutputBase,
      )
    }
  }
  if (!bazelRc) {
    if (sockJson.defaults?.manifest?.bazel?.bazelRc) {
      bazelRc = sockJson.defaults?.manifest?.bazel?.bazelRc
      logger.info(`Using default --bazel-rc from ${SOCKET_JSON}:`, bazelRc)
    }
  }
  if (!out) {
    if (sockJson.defaults?.manifest?.bazel?.out) {
      out = sockJson.defaults?.manifest?.bazel?.out
      logger.info(`Using default --out from ${SOCKET_JSON}:`, out)
    } else {
      out = path.join(cwd, '.socket', 'bazel-manifests')
    }
  }
  if (verbose === undefined) {
    if (sockJson.defaults?.manifest?.bazel?.verbose !== undefined) {
      verbose = sockJson.defaults?.manifest?.bazel?.verbose
      logger.info(`Using default --verbose from ${SOCKET_JSON}:`, verbose)
    } else {
      verbose = false
    }
  }
  if (perRepoTimeout === undefined) {
    if (sockJson.defaults?.manifest?.bazel?.perRepoTimeout !== undefined) {
      perRepoTimeout = sockJson.defaults?.manifest?.bazel?.perRepoTimeout
      logger.info(
        `Using default --per-repo-timeout from ${SOCKET_JSON}:`,
        perRepoTimeout,
      )
    } else {
      // Explicit invocation default; longer than the auto-manifest default
      // because the user is waiting on this single extraction.
      perRepoTimeout = EXPLICIT_PER_REPO_TIMEOUT_MS
    }
  }

  if (verbose) {
    logger.group('- ', parentName, config.commandName, ':')
    logger.group('- flags:', cli.flags)
    logger.groupEnd()
    logger.log('- input:', cli.input)
    logger.groupEnd()
  }

  const wasValidInput = checkCommandInput(outputKind, {
    nook: true,
    test: cli.input.length <= 1,
    message: 'Can only accept one DIR (make sure to escape spaces!)',
    fail: 'received ' + cli.input.length,
  })
  if (!wasValidInput) {
    return
  }

  if (verbose) {
    logger.group()
    logger.info('- cwd:', cwd)
    logger.info('- bazel bin:', bazel)
    logger.info('- out:', out)
    logger.groupEnd()
  }

  if (dryRun) {
    logger.log(DRY_RUN_BAILING_NOW)
    return
  }

  // Ecosystem dispatch: Maven is the default. PyPI is explicit opt-in because
  // its no-lockfile recovery value is narrower than Maven's inline-decl path.
  const wasExplicitEcosystemSelection =
    Array.isArray(ecosystem) && ecosystem.length > 0
  const ecosystems: string[] = wasExplicitEcosystemSelection
    ? (ecosystem as string[])
    : ['maven']

  for (const eco of ecosystems) {
    if (!['maven', 'pypi'].includes(eco)) {
      throw new InputError(
        `Unsupported --ecosystem value: ${eco}. Supported values: maven, pypi.`,
      )
    }
  }

  const outcomes: EcosystemOutcome[] = []

  for (const eco of ecosystems) {
    if (eco === 'maven') {
      // eslint-disable-next-line no-await-in-loop
      const mavenResult = await extractBazelToMaven({
        bazelFlags: bazelFlags as string | undefined,
        bazelOutputBase: bazelOutputBase as string | undefined,
        bazelRc: bazelRc as string | undefined,
        bin: bazel as string | undefined,
        cwd,
        out: out as string,
        perRepoTimeoutMs: perRepoTimeout,
        verbose: Boolean(verbose),
      })
      outcomes.push({
        complete: mavenResult.complete,
        ecosystem: 'maven',
        manifestPaths: mavenResult.manifestPaths,
        status: mavenResult.status,
      })
    } else if (eco === 'pypi') {
      // eslint-disable-next-line no-await-in-loop
      const pypiResult = await extractBazelToPypi({
        bazelFlags: bazelFlags as string | undefined,
        bazelOutputBase: bazelOutputBase as string | undefined,
        bazelRc: bazelRc as string | undefined,
        bin: bazel as string | undefined,
        cwd,
        out: out as string,
        verbose: Boolean(verbose),
        explicitEcosystem: wasExplicitEcosystemSelection,
      })
      outcomes.push({
        ecosystem: 'pypi',
        ...pypiOutcome(pypiResult),
      })
    }
  }

  evaluateEcosystemOutcomes(outcomes, wasExplicitEcosystemSelection)
}
